<template>
    <RegularModal ref="modal" modal-card-class="academy-select-modal" @closed="handleClosed">
        <template v-slot:title>
            {{ translate("academy_modal.select_academy") }}
        </template>
        <template v-slot:body>
            <div v-if="initDone" class="academy-select-modal">
                <template v-if="!pendingItem && !checkingMembership">
                    <label class="label">
                        {{ translate("academy_modal.find_your_academy") }}
                    </label>

                    <QueryInput
                        ref="input"
                        v-model="query"
                        :is-expanded="true"
                        :no-margin="true"
                        :placeholder="translate('academy_modal.type_at_least_two_characters')"
                    />

                    <div class="search-results" v-if="result.length > 0">
                        <div class="results-bar has-text-centered">
                            {{ translate("academy_modal.confirm_choice_label") }}
                        </div>
                        <div class="search-item" v-for="(element, index) in result" :key="index" @click="selectItem(element.item)">
                            <span class="academy-id">#{{ element.item.key }}</span>
                            <span>{{ element.item.value }}</span>
                            <Country :only-flag="true" :country-code="element.item.country" :has-border="true" />
                            <OfficialAcademy :official="element.item.official" />
                        </div>
                    </div>

                    <div>
                        <p class="is-size-6 has-text-centered mt-3">
                            <a :href="urls.helpCenter().choosingAcademy()" target="_blank">
                                {{ translate("academy_modal.what_if_cant_find") }}
                            </a>
                        </p>
                    </div>
                </template>
                <template v-else-if="checkingMembership">
                    <div class="columns is-centered">
                        <LoadingSpinner2 class="has-added-margin" />
                    </div>
                </template>
                <template v-else>
                    <p class="is-size-5 has-text-weight-bold">
                        <template v-if="joinMode !== JoinModeEnum.Blocked">
                            <span class="has-text-grey-light">#{{ pendingItem?.key }}</span>
                        </template>
                        {{ pendingItem?.value }}
                    </p>

                    <AcademyJoinDisclosure v-model="joinAcademy" :join-mode="joinMode" />
                </template>
            </div>
            <div v-else>
                <div class="columns is-centered">
                    <LoadingSpinner2 class="has-added-margin" />
                </div>
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="pendingItem ? backToSearch() : close()">
                    {{ translate(pendingItem ? "common.back" : "common.cancel") }}
                </button>
                <button
                    v-if="pendingItem && joinMode !== JoinModeEnum.Blocked"
                    class="button is-primary"
                    :disabled="joinMode === JoinModeEnum.Mandatory && !joinAcademy"
                    @click="confirmChoice(pendingItem)"
                >
                    <span v-if="joinMode === JoinModeEnum.Mandatory && !joinAcademy" class="icon">
                        <i class="fas fa-lock"></i>
                    </span>
                    <span>{{ translate("common.confirm") }}</span>
                </button>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { nextTick, ref, watch } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import QueryInput from "../Common/QueryInput.vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { createFuseSearch } from "../Common/fuseSearch";
    import urls from "../Urls/urls";
    import OfficialAcademy from "./OfficialAcademy.vue";
    import Country from "../Common/Country.vue";
    import AcademyJoinDisclosure from "./AcademyJoinDisclosure.vue";
    import academiesApi, { JoinMode, JoinModeEnum, LegacyAcademy } from "../Api/academies";
    import HttpError from "../Api/Utils/HttpError";
    import type { default as Fuse, FuseResult } from "fuse.js";

    // The legacy academies listing endpoints return more fields than `LegacyAcademy` declares
    // (`country`, `official`, `hidden`) — this local type reflects what this component actually
    // reads from the response.
    type AcademySearchItem = LegacyAcademy & {
        country: string | null;
        official: boolean;
        hidden?: boolean;
    };

    const props = defineProps<{
        eventIdContext?: number | null;
    }>();

    const emit = defineEmits<{
        confirmed: [item: AcademySearchItem];
    }>();

    const modal = ref<InstanceType<typeof RegularModal> | null>(null);
    const input = ref<InstanceType<typeof QueryInput> | null>(null);

    const query = ref("");
    const result = ref<FuseResult<AcademySearchItem>[]>([]);
    const initDone = ref(false);
    let confirmCallback: ((item: AcademySearchItem) => void) | null = null;
    const pendingItem = ref<AcademySearchItem | null>(null);
    const joinAcademy = ref(false);
    const offerJoin = ref(false);
    const joinMode = ref<JoinMode>(JoinModeEnum.None);
    const checkingMembership = ref(false);
    let membershipRequestId = 0;

    let fuseInstance: Fuse<AcademySearchItem>;

    watch(query, () => {
        search();
    });

    function open(callback?: (item: AcademySearchItem) => void, offerJoinForThisOpen = false): Promise<void> {
        invalidateMembershipCheck();
        confirmCallback = callback ?? null;
        offerJoin.value = offerJoinForThisOpen;
        query.value = "";
        initDone.value = false;
        pendingItem.value = null;
        joinAcademy.value = false;
        joinMode.value = JoinModeEnum.None;
        checkingMembership.value = false;

        modal.value?.open();

        return fetchAcademies()
            .then(academies => {
                fuseInstance = createFuseSearch(
                    academies.filter(academy => !academy.hidden),
                    ["value"],
                    { threshold: 0.2, ignoreLocation: true },
                );
            })
            .catch(handleError)
            .finally(() => {
                initDone.value = true;

                nextTick(() => {
                    input.value?.focus();
                });
            });
    }

    function fetchAcademies(): Promise<AcademySearchItem[]> {
        if (props.eventIdContext) {
            return academiesApi.getAcademies(props.eventIdContext) as unknown as Promise<AcademySearchItem[]>;
        } else {
            return academiesApi.getGlobalAcademies() as unknown as Promise<AcademySearchItem[]>;
        }
    }

    function selectItem(item: AcademySearchItem) {
        if (!offerJoin.value) {
            confirmChoice(item);
            return;
        }

        const requestId = ++membershipRequestId;
        checkingMembership.value = true;
        academiesApi
            .getMyMembership(item.key)
            .then(membership => {
                if (requestId !== membershipRequestId) {
                    return;
                }

                joinMode.value = membership.joinMode;
                joinAcademy.value = false;

                if (membership.joinMode === JoinModeEnum.None) {
                    confirmChoice(item);
                } else {
                    pendingItem.value = item;
                }
            })
            .catch(error => {
                if (requestId === membershipRequestId) {
                    handleError(error);
                }
            })
            .finally(() => {
                if (requestId === membershipRequestId) {
                    checkingMembership.value = false;
                }
            });
    }

    function backToSearch() {
        pendingItem.value = null;
        joinAcademy.value = false;
        joinMode.value = JoinModeEnum.None;
    }

    function confirmChoice(item: AcademySearchItem) {
        const proceed = () => {
            if (typeof confirmCallback === "function") {
                confirmCallback(item);
            }
            emit("confirmed", item);
            close();
        };

        if (joinMode.value === JoinModeEnum.Mandatory) {
            /**
             * Flagged for awareness, not a defect given M7's own framing: **mandatory-join enforcement is
             *   UI-only.** Nothing server-side stops a client calling `POST
             *   /api/academies/{id}/actions/join` directly and skipping the checkbox gate in
             *   `AcademySelectModal.vue`'s `confirmChoice()`. The M7 business decision already accepts this
             *   (the goal is roster/coach-pass visibility, not access control) — worth double-checking Slice 9's
             *   coach-pass logic doesn't assume server-enforced membership before relying on it.
             */
            academiesApi.join(item.key).then(proceed).catch(handleError);
        } else if (joinMode.value === JoinModeEnum.Optional && joinAcademy.value) {
            academiesApi
                .join(item.key)
                .catch((err: HttpError) => {
                    if (err.message !== "academy.member.error.already_member") {
                        handleError(err);
                    }
                })
                .finally(proceed);
        } else {
            proceed();
        }
    }

    function close() {
        invalidateMembershipCheck();
        modal.value?.close();
    }

    function handleClosed() {
        invalidateMembershipCheck();
    }

    function invalidateMembershipCheck() {
        membershipRequestId++;
        checkingMembership.value = false;
    }

    function search() {
        result.value = fuseInstance.search(query.value).slice(0, 15);
    }

    defineExpose({ open });
</script>
